<template>
  <v-container>
    <v-layout>
      <v-flex xs12 sm8 offset-sm2>
        <div class="billing-page">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>

          <h1>Billing &amp; payments</h1>
          <p class="subtitle">Your deposit, installment schedule, and payment methods.</p>

          <div v-if="!dancer || !dancer.id" class="loading-note">Loading your account…</div>

          <template v-else>
            <p v-if="giftSentence" class="gift-sentence">🎁 {{ giftSentence }}</p>

            <div v-if="cardUpdateSuccess" class="card-update-success">
              Your credit card has been updated successfully.
            </div>

            <span v-if="dancer.installments == true">
              <p class="body-copy">Payments are automatically charged to the card on file.</p>
              <div class="card-update-section">
                <p class="card-update-label">Need to use a different card?</p>
                <v-btn small outlined :loading="cardUpdateLoading" @click="updateCard">
                  Update credit card
                </v-btn>
              </div>
              <ul class="payment-list">
                <li>
                  Deposit — Paid: ${{ dancer.initialPayment }}
                  <template v-if="dancer.initialPaymentLink">
                    |
                    <a :href="dancer.initialPaymentLink" target="_blank" rel="noopener">View receipt</a>
                  </template>
                </li>
                <li v-for="n in 6" :key="'p' + n" v-show="dancer['payment' + n + 'Status'] !== 'N/A'">
                  <span v-if="dancer['payment' + n + 'Status'] === 'Paid'">
                    {{ monthLabel(n) }} — Paid: ${{ dancer['payment' + n + 'Due'] }} USD
                    <template v-if="dancer['payment' + n + 'Link']">
                      | <a :href="dancer['payment' + n + 'Link']" target="_blank" rel="noopener">View receipt</a>
                    </template>
                  </span>
                  <span v-else-if="dancer['payment' + n + 'Status'] === 'Overdue'" class="overdue">
                    {{ monthLabel(n) }} OVERDUE: ${{ installmentShownDue(n) }} USD
                    <span v-if="installmentExtrasLabel(n)">{{ installmentExtrasLabel(n) }}</span>
                    <template v-if="dancer['payment' + n + 'Link']">
                      | <a :href="dancer['payment' + n + 'Link']">Pay invoice</a>
                    </template>
                  </span>
                  <span v-else>
                    {{ monthLabel(n) }}: {{ dancer['payment' + n + 'Status'] }} ${{ installmentShownDue(n) }} USD
                    <span v-if="installmentExtrasLabel(n)">{{ installmentExtrasLabel(n) }}</span>
                  </span>
                </li>
              </ul>
            </span>
            <span v-else>
              <p class="body-copy">
                Paid in full
                <template v-if="dancer.initialPaymentLink">
                  | <a :href="dancer.initialPaymentLink" target="_blank" rel="noopener">View receipt</a>
                </template>
              </p>
            </span>

            <section v-if="ledgerRows.length" class="ledger-block">
              <h2>Costume closet &amp; extras</h2>
              <p class="body-copy">
                Each borrow is its own line. Open charges sit on your IABF account until they’re paid.
              </p>
              <ul class="payment-list">
                <li v-for="row in ledgerRows" :key="row.id">
                  {{ row.description || row.itemName }}
                  — {{ ledgerAmountLabel(row) }}
                  <span :class="row.status === 'open' ? 'overdue' : ''">
                    {{ ledgerStatusLabel(row.status) }}
                  </span>
                </li>
              </ul>
              <p v-if="ledgerOpenCents > 0" class="body-copy">
                <strong>Open closet charges: ${{ (ledgerOpenCents / 100).toFixed(0) }}</strong>
              </p>
            </section>

            <a
              class="cancel-policy-link"
              href="https://www.internationaladultballetfestival.com/policies"
              target="_blank"
              rel="noopener"
            >
              Cancellation policy →
            </a>
          </template>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import {
  costumeUsdOnInstallment,
  guestUsdOnInstallment,
  installmentBaseDue,
  installmentBreakdownLabel
} from '@/utils/iabf-installment-extras'

const MONTHS = {
  1: 'January 5',
  2: 'February 5',
  3: 'March 5',
  4: 'April 5',
  5: 'May 5',
  6: 'June 5'
}

export default {
  name: 'IABFBilling',
  data () {
    return {
      cardUpdateLoading: false,
      cardUpdateSuccess: false
    }
  },
  computed: {
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    costumeBalanceDue () {
      const d = this.dancer
      if (!d.costumeOrderCompleted || d.costumeOrderPaid) return 0
      return Number(d.costumeTotalCost) || 0
    },
    guests () {
      return this.$store.getters.guestRegistrations || []
    },
    ledgerRows () {
      return (this.$store.getters.dancerLedger || [])
        .filter(r => r.status !== 'void')
        .slice()
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    },
    ledgerOpenCents () {
      return this.ledgerRows
        .filter(r => r.status === 'open')
        .reduce((sum, r) => sum + (Number(r.amountCents) || 0), 0)
    },
    giftSentence () {
      const IGNORE = ['None', 'Not Yet Selected', '']
      const d = this.dancer
      const gifts = [
        d.registrationGift1,
        d.registrationGift2,
        d.registrationGift3,
        d.registrationGift4
      ].filter(g => g && !IGNORE.includes(g))
      if (!gifts.length) {
        return ''
      }
      let giftList
      if (gifts.length === 1) {
        giftList = gifts[0]
      } else if (gifts.length === 2) {
        giftList = `${gifts[0]} and ${gifts[1]}`
      } else {
        giftList = `${gifts.slice(0, -1).join(', ')}, and ${gifts[gifts.length - 1]}`
      }
      return `As a thank you for coming back to IABF, you've earned free gifts! ${giftList}.`
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    if (this.$route.query.card_updated === 'true') {
      this.cardUpdateSuccess = true
    }
    this.ensureDancer()
  },
  methods: {
    monthLabel (n) {
      return MONTHS[n] || ('Payment ' + n)
    },
    installmentGuestUsd (n) {
      return guestUsdOnInstallment(n, this.guests, this.dancer)
    },
    installmentShownDue (n) {
      const due = Number(this.dancer['payment' + n + 'Due']) || 0
      return installmentBaseDue(due, this.installmentGuestUsd(n))
    },
    installmentExtrasLabel (n) {
      return installmentBreakdownLabel({
        guestUsd: this.installmentGuestUsd(n),
        costumeUsd: costumeUsdOnInstallment(n, this.dancer, this.costumeBalanceDue)
      })
    },
    ledgerAmountLabel (row) {
      const n = Math.round(Number(row.amountCents) || 0)
      if (n === 0) {
        return 'Free'
      }
      return '$' + (n / 100).toFixed(n % 100 === 0 ? 0 : 2)
    },
    ledgerStatusLabel (status) {
      if (status === 'paid') {
        return 'Paid'
      }
      if (status === 'open') {
        return 'On your account'
      }
      return status || ''
    },
    loadLedger () {
      const user = this.$store.getters.user
      const uid = user && user.id
      if (!uid) {
        return
      }
      this.$store.dispatch('loadDancerLedger', { uid }).catch(() => {})
    },
    loadGuests () {
      const email = (this.dancer && this.dancer.email) || ''
      if (!email) {
        return
      }
      this.$store.dispatch('loadGuestRegistrationsForDancer', email.toLowerCase()).catch(() => {})
    },
    ensureDancer () {
      const d = this.dancer
      if (d && d.id) {
        this.loadGuests()
        this.loadLedger()
        return
      }
      const user = this.$store.getters.user
      if (!user) return
      this.$store.dispatch('loadIABFDancerByUid', user.id).then(() => {
        this.loadGuests()
        this.loadLedger()
      })
    },
    updateCard () {
      if (!this.dancer.id) return
      this.cardUpdateLoading = true
      const baseUrl = window.location.origin
      this.$store.dispatch('createCardSetupSession', {
        dancerId: this.dancer.id,
        email: this.dancer.email,
        name: (this.dancer.firstName || '') + ' ' + (this.dancer.lastName || ''),
        successUrl: baseUrl + '/iabf/billing?card_updated=true',
        cancelUrl: baseUrl + '/iabf/billing'
      }).then((url) => {
        window.location.href = url
      }).catch(() => {
        this.cardUpdateLoading = false
        alert('There was an error. Please try again or contact hello@iabf.dance.')
      })
    }
  }
}
</script>

<style scoped>
.billing-page {
  max-width: 780px;
  margin: 0 auto;
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}
.back-link {
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
}
.back-link:hover { color: var(--iabf-ink, #1c1b1f); }
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  margin: 16px 0 8px;
}
.subtitle {
  font-size: 17px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 24px;
}
.body-copy, .payment-list, .gift-sentence {
  font-family: var(--iabf-font-body, inherit);
  font-size: 17px;
  line-height: 1.6;
  color: var(--iabf-ink-muted, #5c5a63);
}
.gift-sentence {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 16px 18px;
  margin-bottom: 20px;
  color: var(--iabf-ink, #1c1b1f);
}
.payment-list {
  padding-left: 20px;
  margin: 16px 0;
}
.payment-list li { margin-bottom: 8px; }
.overdue { color: crimson; }
.card-update-section {
  margin: 16px 0;
  padding: 14px 16px;
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
}
.card-update-label {
  margin: 0 0 10px;
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.card-update-success {
  background: #eef0eb;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
  color: var(--iabf-ink, #1c1b1f);
}
.cancel-policy-link {
  display: inline-block;
  margin-top: 28px;
  padding-top: 16px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  text-decoration: none;
}
.cancel-policy-link:hover {
  text-decoration: underline;
}
.loading-note {
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}
a { color: var(--iabf-primary, #8a957d); }
</style>
